// ProfilesView - Creator profiles management
// Lists, filters, and manages creator profiles to follow

import { profilesApi, type Profile, type ProfileStats } from '../services/profiles-api';

export class ProfilesView {
  private containerEl: HTMLElement | null = null;
  private profiles: Profile[] = [];
  private stats: ProfileStats | null = null;
  private brands: string[] = [];
  private saveTabs: string[] = [];
  private isLoading: boolean = false;
  private searchQuery: string = '';
  private filterPlatform: string = '';
  private filterBrand: string = '';
  private filterSaveTab: string = '';
  private selectedProfile: Profile | null = null;

  constructor() {
    this.containerEl = document.getElementById('profiles-view');
  }

  async load(): Promise<void> {
    if (!this.containerEl) return;
    this.render();
    await this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    this.renderContent();

    try {
      const [result, stats, brands, saveTabs] = await Promise.all([
        profilesApi.list({
          platform: this.filterPlatform || undefined,
          brand: this.filterBrand || undefined,
          saveTab: this.filterSaveTab || undefined,
          search: this.searchQuery || undefined,
          limit: 100
        }),
        profilesApi.getStats(),
        profilesApi.getBrands(),
        profilesApi.getSaveTabs()
      ]);

      this.profiles = result.profiles;
      this.stats = stats;
      this.brands = brands;
      this.saveTabs = saveTabs;
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }

    this.isLoading = false;
    this.renderContent();
  }

  private render(): void {
    if (!this.containerEl) return;

    this.containerEl.innerHTML = `
      <!-- FilterBar -->
      <header class="filter-bar">
        <div class="filter-bar-left">
          <h1 class="filter-bar-title">Profiles</h1>
          <span class="filter-bar-count" id="profiles-count">0</span>
        </div>
        <div class="filter-bar-center">
          <div class="filter-bar-search">
            <span class="material-symbols-outlined">search</span>
            <input type="text" id="profiles-search" placeholder="Search profiles...">
          </div>
          <select class="filter-bar-select" id="profiles-platform-filter">
            <option value="">All Platforms</option>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
            <option value="tiktok">TikTok</option>
            <option value="linkedin">LinkedIn</option>
          </select>
          <select class="filter-bar-select" id="profiles-brand-filter">
            <option value="">All Brands</option>
          </select>
          <select class="filter-bar-select" id="profiles-tab-filter">
            <option value="">All Tabs</option>
          </select>
        </div>
        <div class="filter-bar-right">
          <button class="filter-bar-action" id="profiles-add" title="Add Profile">
            <span class="material-symbols-outlined">person_add</span>
          </button>
          <button class="filter-bar-action" id="profiles-import" title="Import CSV">
            <span class="material-symbols-outlined">upload_file</span>
          </button>
          <button class="filter-bar-action" id="profiles-export" title="Export">
            <span class="material-symbols-outlined">download</span>
          </button>
          <button class="filter-bar-action" id="profiles-refresh" title="Refresh">
            <span class="material-symbols-outlined">refresh</span>
          </button>
          <button class="filter-bar-action" id="profiles-sync-all" title="Sync All Monitored">
            <span class="material-symbols-outlined">cloud_sync</span>
          </button>
        </div>
      </header>

      <!-- Stats Bar -->
      <div class="profiles-stats-bar" id="profiles-stats">
        <!-- Rendered dynamically -->
      </div>

      <!-- Main Content -->
      <div class="profiles-content" id="profiles-content">
        <!-- Rendered dynamically -->
      </div>

      <!-- Profile Modal -->
      <div class="profiles-modal hidden" id="profiles-modal">
        <div class="profiles-modal-content">
          <div class="profiles-modal-header">
            <h2 id="profiles-modal-title">Add Profile</h2>
            <button class="profiles-modal-close" id="profiles-modal-close">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="profiles-modal-body" id="profiles-modal-body">
            <!-- Form rendered here -->
          </div>
        </div>
      </div>
    `;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Search
    const searchInput = this.containerEl?.querySelector('#profiles-search') as HTMLInputElement;
    let searchTimeout: ReturnType<typeof setTimeout>;
    searchInput?.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.searchQuery = searchInput.value;
        this.loadData();
      }, 300);
    });

    // Filters
    this.containerEl?.querySelector('#profiles-platform-filter')?.addEventListener('change', (e) => {
      this.filterPlatform = (e.target as HTMLSelectElement).value;
      this.loadData();
    });

    this.containerEl?.querySelector('#profiles-brand-filter')?.addEventListener('change', (e) => {
      this.filterBrand = (e.target as HTMLSelectElement).value;
      this.loadData();
    });

    this.containerEl?.querySelector('#profiles-tab-filter')?.addEventListener('change', (e) => {
      this.filterSaveTab = (e.target as HTMLSelectElement).value;
      this.loadData();
    });

    // Actions
    this.containerEl?.querySelector('#profiles-add')?.addEventListener('click', () => this.showAddModal());
    this.containerEl?.querySelector('#profiles-import')?.addEventListener('click', () => this.showImportModal());
    this.containerEl?.querySelector('#profiles-export')?.addEventListener('click', () => this.exportProfiles());
    this.containerEl?.querySelector('#profiles-refresh')?.addEventListener('click', () => this.loadData());
    this.containerEl?.querySelector('#profiles-sync-all')?.addEventListener('click', () => this.syncAllProfiles());

    // Modal close
    this.containerEl?.querySelector('#profiles-modal-close')?.addEventListener('click', () => this.hideModal());
    this.containerEl?.querySelector('#profiles-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('profiles-modal')) {
        this.hideModal();
      }
    });
  }

  private renderContent(): void {
    this.renderStats();
    this.renderFilters();
    this.renderList();
  }

  private renderStats(): void {
    const statsEl = this.containerEl?.querySelector('#profiles-stats');
    if (!statsEl || !this.stats) return;

    statsEl.innerHTML = `
      <div class="profiles-stat">
        <span class="profiles-stat-value">${this.stats.total}</span>
        <span class="profiles-stat-label">Total</span>
      </div>
      ${Object.entries(this.stats.byPlatform).map(([platform, count]) => `
        <div class="profiles-stat">
          <span class="profiles-stat-value">${count}</span>
          <span class="profiles-stat-label">${platform}</span>
        </div>
      `).join('')}
    `;

    // Update count badge
    const countEl = this.containerEl?.querySelector('#profiles-count');
    if (countEl) countEl.textContent = String(this.profiles.length);
  }

  private renderFilters(): void {
    // Populate brand filter
    const brandFilter = this.containerEl?.querySelector('#profiles-brand-filter') as HTMLSelectElement;
    if (brandFilter) {
      const currentValue = brandFilter.value;
      brandFilter.innerHTML = '<option value="">All Brands</option>' +
        this.brands.map(b => `<option value="${b}"${b === currentValue ? ' selected' : ''}>${b}</option>`).join('');
    }

    // Populate save tab filter
    const tabFilter = this.containerEl?.querySelector('#profiles-tab-filter') as HTMLSelectElement;
    if (tabFilter) {
      const currentValue = tabFilter.value;
      tabFilter.innerHTML = '<option value="">All Tabs</option>' +
        this.saveTabs.map(t => `<option value="${t}"${t === currentValue ? ' selected' : ''}>${t}</option>`).join('');
    }
  }

  private renderList(): void {
    const contentEl = this.containerEl?.querySelector('#profiles-content');
    if (!contentEl) return;

    if (this.isLoading) {
      contentEl.innerHTML = `
        <div class="profiles-loading">
          <span class="material-symbols-outlined spinning">progress_activity</span>
          <span>Loading profiles...</span>
        </div>
      `;
      return;
    }

    if (this.profiles.length === 0) {
      contentEl.innerHTML = `
        <div class="profiles-empty">
          <span class="material-symbols-outlined">group</span>
          <h2>No profiles yet</h2>
          <p>Add creators to monitor their content</p>
          <button class="profiles-empty-btn" id="profiles-empty-add">Add Profile</button>
        </div>
      `;
      contentEl.querySelector('#profiles-empty-add')?.addEventListener('click', () => this.showAddModal());
      return;
    }

    contentEl.innerHTML = `
      <div class="profiles-table">
        <div class="profiles-table-header">
          <div class="profiles-col-user">Profile</div>
          <div class="profiles-col-brand">Brand</div>
          <div class="profiles-col-monitors">Monitoring</div>
          <div class="profiles-col-tab">Save Tab</div>
          <div class="profiles-col-stats">Stats</div>
          <div class="profiles-col-actions">Actions</div>
        </div>
        <div class="profiles-table-body">
          ${this.profiles.map(p => this.renderProfileRow(p)).join('')}
        </div>
      </div>
    `;

    // Add row click handlers
    contentEl.querySelectorAll('.profiles-row').forEach(row => {
      row.querySelector('.profiles-sync')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = (row as HTMLElement).dataset.id;
        if (id) await this.syncProfile(id);
      });

      row.querySelector('.profiles-edit')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (row as HTMLElement).dataset.id;
        const profile = this.profiles.find(p => p.id === id);
        if (profile) this.showEditModal(profile);
      });

      row.querySelector('.profiles-delete')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = (row as HTMLElement).dataset.id;
        if (id && confirm('Delete this profile?')) {
          await this.deleteProfile(id);
        }
      });
    });
  }

  private renderProfileRow(profile: Profile): string {
    const platformIcon = this.getPlatformIcon(profile.platform);
    const monitors = [
      profile.monitorStories && 'stories',
      profile.monitorReels && 'reels',
      profile.monitorPosts && 'posts',
      profile.monitorCarousel && 'carousel',
      profile.monitorAds && 'ads',
      profile.monitorVlog && 'vlog'
    ].filter(Boolean);

    return `
      <div class="profiles-row" data-id="${profile.id}">
        <div class="profiles-col-user">
          <div class="profiles-avatar">${profile.username.charAt(0).toUpperCase()}</div>
          <div class="profiles-user-info">
            <span class="profiles-username">${profile.username}</span>
            <span class="profiles-platform">
              <span class="material-symbols-outlined">${platformIcon}</span>
              ${profile.platform}
            </span>
          </div>
        </div>
        <div class="profiles-col-brand">
          ${profile.brand ? `<span class="profiles-brand-badge">${profile.brand}</span>` : '-'}
          ${profile.tipo ? `<span class="profiles-tipo">${profile.tipo}</span>` : ''}
        </div>
        <div class="profiles-col-monitors">
          <div class="profiles-monitor-icons">
            ${monitors.length > 0 ? monitors.map(m => `
              <span class="profiles-monitor-icon active" title="${m}">
                <span class="material-symbols-outlined">${this.getMonitorIcon(m as string)}</span>
              </span>
            `).join('') : '<span class="profiles-no-monitors">None</span>'}
          </div>
        </div>
        <div class="profiles-col-tab">
          ${profile.saveTab || '-'}
        </div>
        <div class="profiles-col-stats">
          <span class="profiles-stat-mini" title="Reels">${profile.reelsCount}</span>
          <span class="profiles-stat-mini" title="Posts">${profile.postsCount}</span>
          <span class="profiles-stat-mini" title="Stories">${profile.storiesCount}</span>
        </div>
        <div class="profiles-col-actions">
          <button class="profiles-sync" title="Sync Content">
            <span class="material-symbols-outlined">cloud_download</span>
          </button>
          <button class="profiles-edit" title="Edit">
            <span class="material-symbols-outlined">edit</span>
          </button>
          <button class="profiles-delete" title="Delete">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
    `;
  }

  private getPlatformIcon(platform: string): string {
    switch (platform) {
      case 'instagram': return 'photo_camera';
      case 'youtube': return 'smart_display';
      case 'tiktok': return 'music_note';
      case 'linkedin': return 'business';
      default: return 'public';
    }
  }

  private getMonitorIcon(type: string): string {
    switch (type) {
      case 'stories': return 'auto_stories';
      case 'reels': return 'slow_motion_video';
      case 'posts': return 'image';
      case 'carousel': return 'view_carousel';
      case 'ads': return 'campaign';
      case 'vlog': return 'videocam';
      default: return 'visibility';
    }
  }

  private showAddModal(): void {
    this.selectedProfile = null;
    const titleEl = this.containerEl?.querySelector('#profiles-modal-title');
    if (titleEl) titleEl.textContent = 'Add Profile';
    this.renderForm();
    this.containerEl?.querySelector('#profiles-modal')?.classList.remove('hidden');
  }

  private showEditModal(profile: Profile): void {
    this.selectedProfile = profile;
    const titleEl = this.containerEl?.querySelector('#profiles-modal-title');
    if (titleEl) titleEl.textContent = 'Edit Profile';
    this.renderForm();
    this.containerEl?.querySelector('#profiles-modal')?.classList.remove('hidden');
  }

  private hideModal(): void {
    this.containerEl?.querySelector('#profiles-modal')?.classList.add('hidden');
    this.selectedProfile = null;
  }

  private renderForm(): void {
    const bodyEl = this.containerEl?.querySelector('#profiles-modal-body');
    if (!bodyEl) return;

    const p = this.selectedProfile;

    bodyEl.innerHTML = `
      <form class="profiles-form" id="profiles-form">
        <div class="profiles-form-row">
          <label>Username *</label>
          <input type="text" name="username" value="${p?.username || ''}" required>
        </div>
        <div class="profiles-form-row">
          <label>Platform</label>
          <select name="platform" id="profile-platform-select">
            <option value="instagram"${p?.platform === 'instagram' ? ' selected' : ''}>Instagram</option>
            <option value="youtube"${p?.platform === 'youtube' ? ' selected' : ''}>YouTube</option>
            <option value="tiktok"${p?.platform === 'tiktok' ? ' selected' : ''}>TikTok</option>
            <option value="linkedin"${p?.platform === 'linkedin' ? ' selected' : ''}>LinkedIn</option>
          </select>
        </div>

        <div class="profiles-form-row" id="linkedin-fetch-section" style="${p?.platform === 'linkedin' ? '' : 'display: none;'}">
          <label>LinkedIn Profile URL</label>
          <div style="display: flex; gap: 8px;">
            <input type="url" id="linkedin-fetch-url" placeholder="https://linkedin.com/in/username">
            <button type="button" id="btn-fetch-linkedin" class="profiles-form-submit" style="white-space: nowrap;">Fetch</button>
          </div>
          <small>Get profile data from LinkedIn URL</small>
        </div>
        <div class="profiles-form-row">
          <label>Brand</label>
          <input type="text" name="brand" value="${p?.brand || ''}" list="brands-list">
          <datalist id="brands-list">
            ${this.brands.map(b => `<option value="${b}">`).join('')}
          </datalist>
        </div>
        <div class="profiles-form-row">
          <label>Type</label>
          <input type="text" name="tipo" value="${p?.tipo || ''}" placeholder="e.g. guru">
        </div>
        <div class="profiles-form-row">
          <label>Save Tab</label>
          <input type="text" name="saveTab" value="${p?.saveTab || ''}" list="tabs-list">
          <datalist id="tabs-list">
            ${this.saveTabs.map(t => `<option value="${t}">`).join('')}
          </datalist>
        </div>
        <div class="profiles-form-row">
          <label>Monitoring</label>
          <div class="profiles-checkboxes">
            <label><input type="checkbox" name="monitorStories"${p?.monitorStories ? ' checked' : ''}> Stories</label>
            <label><input type="checkbox" name="monitorReels"${p?.monitorReels ? ' checked' : ''}> Reels</label>
            <label><input type="checkbox" name="monitorPosts"${p?.monitorPosts ? ' checked' : ''}> Posts</label>
            <label><input type="checkbox" name="monitorCarousel"${p?.monitorCarousel ? ' checked' : ''}> Carousel</label>
            <label><input type="checkbox" name="monitorAds"${p?.monitorAds ? ' checked' : ''}> Ads</label>
            <label><input type="checkbox" name="monitorVlog"${p?.monitorVlog ? ' checked' : ''}> Vlog</label>
          </div>
        </div>
        <div class="profiles-form-actions">
          <button type="button" class="profiles-form-cancel">Cancel</button>
          <button type="submit" class="profiles-form-submit">${p ? 'Save Changes' : 'Add Profile'}</button>
        </div>
      </form>
    `;

    // Form handlers
    const form = bodyEl.querySelector('#profiles-form') as HTMLFormElement;
    form?.querySelector('.profiles-form-cancel')?.addEventListener('click', () => this.hideModal());
    form?.addEventListener('submit', (e) => this.handleFormSubmit(e));

    // Platform change - show/hide LinkedIn fetch section
    const platformSelect = form?.querySelector('#profile-platform-select') as HTMLSelectElement;
    const linkedinSection = form?.querySelector('#linkedin-fetch-section') as HTMLElement;
    platformSelect?.addEventListener('change', () => {
      if (linkedinSection) {
        linkedinSection.style.display = platformSelect.value === 'linkedin' ? '' : 'none';
      }
    });

    // LinkedIn fetch button
    form?.querySelector('#btn-fetch-linkedin')?.addEventListener('click', async () => {
      const urlInput = form.querySelector('#linkedin-fetch-url') as HTMLInputElement;
      const url = urlInput?.value?.trim();
      if (!url) {
        alert('Enter LinkedIn URL');
        return;
      }

      const btn = form.querySelector('#btn-fetch-linkedin') as HTMLButtonElement;
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Fetching...';

      try {
        const response = await fetch('/api/profiles/linkedin/fetch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profileUrl: url })
        });

        const result = await response.json();
        if (result.success && result.data) {
          // Auto-fill form fields
          const usernameInput = form.querySelector('input[name="username"]') as HTMLInputElement;
          if (usernameInput && result.data.username) usernameInput.value = result.data.username;

          alert('Profile data fetched! Review and save.');
        } else {
          alert('Error: ' + (result.error || 'Failed to fetch profile'));
        }
      } catch (error) {
        alert('Failed to fetch LinkedIn profile');
      } finally {
        btn.disabled = false;
        btn.textContent = originalText || 'Fetch';
      }
    });
  }

  private async handleFormSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const data = {
      username: formData.get('username') as string,
      platform: formData.get('platform') as Profile['platform'],
      brand: formData.get('brand') as string || undefined,
      tipo: formData.get('tipo') as string || undefined,
      saveTab: formData.get('saveTab') as string || undefined,
      monitorStories: formData.has('monitorStories'),
      monitorReels: formData.has('monitorReels'),
      monitorPosts: formData.has('monitorPosts'),
      monitorCarousel: formData.has('monitorCarousel'),
      monitorAds: formData.has('monitorAds'),
      monitorVlog: formData.has('monitorVlog')
    };

    try {
      if (this.selectedProfile) {
        await profilesApi.update(this.selectedProfile.id, data);
      } else {
        await profilesApi.create(data);
      }
      this.hideModal();
      await this.loadData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to save profile');
    }
  }

  private async deleteProfile(id: string): Promise<void> {
    try {
      await profilesApi.delete(id);
      await this.loadData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete profile');
    }
  }

  private showImportModal(): void {
    alert('CSV Import: Upload a CSV file with columns: username, platform, brand, tipo, stories, reels, posts, carousel, ads, vlog, save_tab');
    // TODO: Implement file upload and parsing
  }

  private async exportProfiles(): Promise<void> {
    try {
      const csv = await profilesApi.export('csv') as string;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'profiles.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to export');
    }
  }

  private async syncProfile(id: string): Promise<void> {
    const profile = this.profiles.find(p => p.id === id);
    const btn = this.containerEl?.querySelector(`.profiles-row[data-id="${id}"] .profiles-sync`);

    if (btn) {
      btn.classList.add('syncing');
      (btn as HTMLElement).style.pointerEvents = 'none';
    }

    try {
      const result = await profilesApi.syncProfile(id);
      const name = profile?.username || 'Profile';

      if (result.success) {
        alert(`Synced ${result.syncedCount} items from @${name}`);
      } else {
        alert(`Sync failed for @${name}: ${result.errors?.[0] || 'Unknown error'}`);
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to sync profile');
    } finally {
      if (btn) {
        btn.classList.remove('syncing');
        (btn as HTMLElement).style.pointerEvents = '';
      }
    }
  }

  private async syncAllProfiles(): Promise<void> {
    const btn = this.containerEl?.querySelector('#profiles-sync-all');

    if (btn) {
      btn.classList.add('syncing');
      (btn as HTMLElement).style.pointerEvents = 'none';
    }

    try {
      const result = await profilesApi.syncAll();
      alert(`Synced ${result.summary.synced} items from ${result.summary.success}/${result.summary.total} profiles`);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to sync profiles');
    } finally {
      if (btn) {
        btn.classList.remove('syncing');
        (btn as HTMLElement).style.pointerEvents = '';
      }
    }
  }
}
